import {Form, type FormSubmitHandler, showNotification} from 'platform/components';

import {useState} from 'react';

import {isNil, isNotNil} from 'ramda';

import {
  getCheckoutProfileErrors,
  type CheckoutProfileRule,
  type CheckoutProfileRuleWrite,
  type CheckoutProfileError,
  useCreateCheckoutProfileRuleMutation,
  useUpdateCheckoutProfileRuleMutation,
} from '@dms/api/sales';
import i18n from '@dms/i18n';
import {settingsRoutes} from '@dms/routes';
import {handleApiError, queryParams, refreshDmsDatagrid} from '@dms/shared';

import {type ApiError, composePath, useNavigate} from 'shared/web';

import {CHECKOUT_PROFILE_TAB_IDS} from '../../../constants/CHECKOUT_PROFILE_TAB_IDS';
import {EMPTY_CHECKOUT_RULE_CONDITIONS} from '../../../constants/EMPTY_CHECKOUT_RULE_CONDITIONS';
import {CheckoutRuleFormSchema} from '../types/CheckoutRuleFormSchema';
import type {CheckoutRuleFormValues} from '../types/CheckoutRuleFormValues';
import {CheckoutRuleFormContent} from './CheckoutRuleFormContent';

interface Props {
  initialRule?: CheckoutProfileRule;
  branchId?: string;
  checkoutProfileId?: string;
  returnProfileId?: string;
}

const DEFAULT_CHECKOUT_RULE_PRIORITY = 100;

export function CheckoutRuleEditor(props: Props) {
  const [rule, setRule] = useState(props.initialRule);
  const [serverResponse, setServerResponse] = useState<{
    errors: CheckoutProfileError[];
    values: CheckoutProfileRuleWrite;
  }>();

  const [createRule] = useCreateCheckoutProfileRuleMutation();
  const [updateRule] = useUpdateCheckoutProfileRuleMutation();
  const navigate = useNavigate();
  const returnPath = isNotNil(props.returnProfileId)
    ? composePath(settingsRoutes.checkoutProfileDetail, {
        params: {id: props.returnProfileId},
        isSearchParamsPreserved: false,
        queryParams: new URLSearchParams({
          [queryParams.COMPONENT_SECTIONS_TAB]: CHECKOUT_PROFILE_TAB_IDS.assignmentRules,
        }).toString(),
      })
    : settingsRoutes.checkoutAssignmentRules;

  const onSubmit: FormSubmitHandler<CheckoutRuleFormValues> = async (
    formValues,
    _setFieldErrors,
    reset,
    blocker
  ) => {
    const values = CheckoutRuleFormSchema.parse(formValues);
    setServerResponse(undefined);
    const request = rule
      ? updateRule({checkoutProfileRuleId: rule.id, body: values})
      : createRule({body: values});

    await request
      .unwrap()
      .then((saved) => {
        setRule(saved);
        reset(saved);
        refreshDmsDatagrid('checkout-rule');
        refreshDmsDatagrid('checkout-profile-rule');
        showNotification.success(i18n.t('general.notifications.changesSaved'));

        if (isNil(rule) && !blocker?.wasSubmittedFromBlocker) {
          navigate(
            composePath(settingsRoutes.checkoutAssignmentRuleDetail, {
              params: {id: saved.id},
              isSearchParamsPreserved: false,
              queryParams: isNotNil(props.returnProfileId)
                ? new URLSearchParams({returnProfileId: props.returnProfileId}).toString()
                : undefined,
            })
          );
        }
      })
      .catch((error: ApiError) => {
        blocker?.reportApiFailure?.();
        const refusal = getCheckoutProfileErrors(error);

        if (refusal) {
          setServerResponse({errors: refusal.errors, values});
        } else {
          handleApiError(error);
        }
      });
  };

  return (
    <Form<CheckoutRuleFormValues>
      experimentalZodSchema={CheckoutRuleFormSchema}
      mode="onChange"
      defaultValues={
        rule ?? {
          name: '',
          branchId: props.branchId ?? '',
          checkoutProfileId: props.checkoutProfileId ?? '',
          sourceType: null,
          priority: DEFAULT_CHECKOUT_RULE_PRIORITY,
          isActive: true,
          conditions: {...EMPTY_CHECKOUT_RULE_CONDITIONS},
        }
      }
      onSubmit={onSubmit}
      isFullHeight
      shouldWatchForUnsavedChanges
      data-testid="checkout-rule-form"
    >
      {(control, formApi, navigateWithoutBlocker) => (
        <CheckoutRuleFormContent
          control={control}
          formApi={formApi}
          rule={rule}
          contextProfileId={props.checkoutProfileId}
          returnPath={returnPath}
          onDiscard={() => navigateWithoutBlocker(returnPath)}
          serverResponse={serverResponse}
        />
      )}
    </Form>
  );
}
